/*
 * Zoom on hover for the product gallery.
 *
 * Dawn ships `magnify.js`, and its "zoom on hover" option does not zoom on
 * hover -- it waits for a click and only then follows the cursor. Ahmad asked
 * for the Amazon behaviour: no zoom button at all, and the magnified view
 * appears the moment the pointer is over the image.
 *
 * Two things this does differently from Dawn's version:
 *
 *   - It listens on the document rather than binding each <img> once at load.
 *     product-info.js replaces the whole gallery when a variant changes, which
 *     drops any listener attached to the old element.
 *   - The overlay is pointer-events: none (see main-product.liquid), so the
 *     cursor keeps hitting the image underneath. That is what lets a single
 *     mousemove/mouseleave pair on the container drive the whole thing.
 *
 * Dawn already hides the zoom button and the lightbox opener on hover-capable
 * devices when image_zoom is 'hover' -- see the (hover: hover) block in
 * section-main-product.css. Touch devices keep both and open the lightbox.
 */
(function () {
  // The theme editor re-runs this file on every section render; the document
  // listeners below must only ever be bound once.
  if (window.__prinsteyZoomBound) return;
  window.__prinsteyZoomBound = true;

  const ZOOM = 2.5;
  const MAX_SOURCE_WIDTH = 1946; // the largest width product-thumbnail.liquid asks the CDN for
  const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

  let overlay = null;
  let container = null;

  function highResSource(image) {
    const src = image.currentSrc || image.src;
    if (!src) return '';
    const wanted = Math.min(
      MAX_SOURCE_WIDTH,
      Math.ceil(image.clientWidth * ZOOM * (window.devicePixelRatio || 1))
    );
    return /[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, '$1' + wanted) : src;
  }

  function track(event) {
    if (!overlay || !container) return;
    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    overlay.style.backgroundPosition = clamp(x) + '% ' + clamp(y) + '%';
  }

  function clamp(value) {
    return Math.max(0, Math.min(100, value));
  }

  function teardown() {
    if (container) {
      container.removeEventListener('mousemove', track);
      container.removeEventListener('mouseleave', teardown);
    }
    if (overlay && overlay.parentElement) overlay.parentElement.removeChild(overlay);
    overlay = null;
    container = null;
  }

  function open(image, event) {
    const source = highResSource(image);
    if (!source) return;

    const host = image.parentElement; // .product__media, which is position: relative
    if (!host) return;

    teardown();
    container = host;

    // Paint only once the full-size file is decoded, otherwise the first
    // frame is a stretched thumbnail.
    const preload = new Image();
    preload.onload = function () {
      // The pointer may have left again while that was downloading.
      if (container !== host || !host.isConnected) return;
      overlay = document.createElement('div');
      overlay.className = 'image-magnify-full-size';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.style.backgroundImage = "url('" + source + "')";
      overlay.style.backgroundSize = ZOOM * 100 + '%';
      overlay.style.backgroundRepeat = 'no-repeat';
      overlay.style.backgroundColor = 'var(--gradient-background)';
      host.insertBefore(overlay, image);
      track(event);
    };
    preload.src = source;

    host.addEventListener('mousemove', track);
    host.addEventListener('mouseleave', teardown);
  }

  document.addEventListener('mouseover', function (event) {
    if (!FINE_POINTER.matches) return;
    const target = event.target;
    if (!target || !target.classList || !target.classList.contains('image-magnify-hover')) return;
    if (container === target.parentElement) return; // already zooming this one
    open(target, event);
  });

  // A variant swap can replace the gallery while the overlay is open, which
  // would otherwise leave it stranded over the new image.
  document.addEventListener('mouseout', function (event) {
    if (container && !container.isConnected) teardown();
  });

  /*
   * Dawn's product-info.js calls a bare enableZoomOnHover(2) inside
   * updateMedia() whenever a variant change re-renders the gallery, to re-bind
   * magnify.js to the new <img> elements. magnify.js declared that as a global
   * function; this file is an IIFE and does not need re-binding at all, because
   * it listens on the document.
   *
   * It still has to EXIST. Without it that call throws a ReferenceError, which
   * aborts handleUpdateProductInfo before it reaches updateSourceFromDestination
   * -- so the price silently stops following the variant. Nothing else breaks
   * and nothing appears in the console unless you are watching for it, which is
   * exactly how it went unnoticed the first time.
   */
  window.enableZoomOnHover = function () {};
})();
